import { isEmpty, isNil } from 'lodash';
import {
  ControlsState,
  FilterOperation,
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  GroupOperation,
  SortOperation,
  SortOption,
  SortSelectionState,
} from '../types';

type FilterDispatchAction = {
  type: 'filter';
  operation: FilterOperation;
  payload: Partial<FilterSelectionState>;
};

type SortDispatchAction = {
  type: 'sort';
  operation: SortOperation;
  payload: SortOption;
};

type GroupDispatchAction = {
  type: 'group';
  operation: GroupOperation;
  index?: number;
  payload: { filter?: FilterOption; selection?: FilterSelection };
};

type ResetDispatchAction = {
  type: 'reset';
  payload: ControlsState;
};

type ControlsDispatchAction =
  | FilterDispatchAction
  | SortDispatchAction
  | GroupDispatchAction
  | ResetDispatchAction;

// Each control keeps two views of itself: the maps/lists the query is built from
// (`filters`, `sorts`, `groups`) and the rows its menu shows (`selectedFilters`,
// `selectedSorts`, `selectedGroups`). They differ - a menu row exists before anything
// is selected on it, and the query state ignores those - so both live here and every
// action updates them together, rather than leaving call sites to write each half.
export function reducer(state: ControlsState, action: ControlsDispatchAction): ControlsState {
  switch (action.type) {
    case 'filter': {
      return { ...state, ...filterReducer(state, action) };
    }

    case 'sort': {
      return { ...state, ...sortReducer(state, action) };
    }

    case 'group': {
      return { ...state, ...groupReducer(state, action) };
    }

    case 'reset': {
      return action.payload;
    }

    default: {
      return state;
    }
  }
}

type FilterState = Pick<ControlsState, 'filters' | 'selectedFilters'>;

function filterReducer(state: FilterState, action: FilterDispatchAction): FilterState {
  const { operation, payload } = action;
  const { filter, selection, negate } = payload;

  // clearing everything isn't scoped to one filter, so it runs before the guard below
  if (operation === 'removeAllFilters') {
    return { filters: new Map(), selectedFilters: [] };
  }

  if (isNil(filter) || isNil(filter.uuid)) {
    return state;
  }

  const filters = new Map(state.filters);
  let selectedFilters = state.selectedFilters;

  switch (operation) {
    case 'addFilter':
      // a menu row exists as soon as the filter is picked, before any selection
      selectedFilters = upsertSelectedFilter(selectedFilters, filter);
      if (!isNil(selection)) {
        filters.set(filter.uuid, { filter, selection, negate: false });
      } else {
        filters.delete(filter.uuid);
      }
      break;
    case 'removeFilter':
      selectedFilters = selectedFilters.filter(item => item.uuid !== filter.uuid);
      filters.delete(filter.uuid);
      break;
    case 'addSelections':
    case 'updateSelections':
      if (!isNil(selection)) {
        filters.set(filter.uuid, { filter, selection, negate: negate ?? false });
      } else {
        filters.delete(filter.uuid);
      }
      break;
    case 'removeAllSelections':
      // the row stays in the menu with nothing selected
      filters.delete(filter.uuid);
      break;
  }
  return { filters, selectedFilters };
}

function upsertSelectedFilter(
  selectedFilters: FilterOption[],
  filter: FilterOption,
): FilterOption[] {
  if (!selectedFilters.some(item => item.uuid === filter.uuid)) {
    return [...selectedFilters, filter];
  }
  return selectedFilters.map(item => (item.uuid === filter.uuid ? filter : item));
}

type SortState = Pick<ControlsState, 'sorts' | 'selectedSorts'>;

function sortReducer(state: SortState, action: SortDispatchAction): SortState {
  const { operation, payload } = action;
  if (isNil(payload.uuid)) {
    return state;
  }

  let selectedSorts = state.selectedSorts;

  switch (operation) {
    case 'addSort':
      selectedSorts = [...selectedSorts, payload];
      break;
    case 'prependSort':
      selectedSorts = [payload, ...selectedSorts];
      break;
    case 'updateSort':
      selectedSorts = selectedSorts.map(item => (item.uuid === payload.uuid ? payload : item));
      break;
    case 'removeSort':
      selectedSorts = selectedSorts.filter(item => item.uuid !== payload.uuid);
      break;
  }

  return { selectedSorts, sorts: toSortSelections(selectedSorts) };
}

// sort precedence is the order of the menu rows, so the map the query reads is derived
// from that list rather than tracked alongside it
function toSortSelections(selectedSorts: SortOption[]): ControlsState['sorts'] {
  const sorts = new Map<string, SortSelectionState>();
  selectedSorts.forEach(sort => {
    if (!isNil(sort.uuid)) {
      sorts.set(sort.uuid, { sort, direction: sort.direction ?? 'ASC' });
    }
  });
  return sorts;
}

type GroupState = Pick<ControlsState, 'groups' | 'selectedGroups'>;

function groupReducer(state: GroupState, action: GroupDispatchAction): GroupState {
  const { operation, index, payload } = action;
  const { filter, selection } = payload;

  // these aren't scoped to one filter, so they run before the guards below
  if (operation === 'removeAllGroups') {
    return { groups: [], selectedGroups: [] };
  }
  if (operation === 'removeGroup') {
    return isNil(index) ? state : removeGroupAt(state, index);
  }

  if (isNil(filter) || isNil(filter.uuid)) {
    return state;
  }

  // a new group starts as one menu row with nothing selected on it yet
  if (operation === 'addGroup') {
    return {
      groups: [...state.groups, new Map()],
      selectedGroups: [...state.selectedGroups, [filter]],
    };
  }

  if (isNil(index) || isNil(state.groups[index])) {
    return state;
  }

  const groupSelections = new Map(state.groups[index]);
  let groupFilters = state.selectedGroups[index] ?? [];

  switch (operation) {
    case 'addFilter':
      groupFilters = [...groupFilters, filter];
      break;
    case 'updateFilter':
      // swapping the component clears whatever was selected on the old one
      groupFilters = groupFilters.map(item => (item.uuid === filter.uuid ? filter : item));
      groupSelections.delete(filter.uuid);
      break;
    case 'removeFilter':
      groupFilters = groupFilters.filter(item => item.uuid !== filter.uuid);
      groupSelections.delete(filter.uuid);
      break;
    case 'updateSelections':
      if (!isNil(selection) && !isEmpty(selection)) {
        groupSelections.set(filter.uuid, { filter, selection, negate: false });
      } else {
        groupSelections.delete(filter.uuid);
      }
      break;
  }

  // a group with no filters left has nothing to group by
  if (isEmpty(groupFilters)) {
    return removeGroupAt(state, index);
  }

  const groups = [...state.groups];
  const selectedGroups = [...state.selectedGroups];
  groups[index] = groupSelections;
  selectedGroups[index] = groupFilters;
  return { groups, selectedGroups };
}

function removeGroupAt(state: GroupState, index: number): GroupState {
  return {
    groups: state.groups.filter((_, i) => i !== index),
    selectedGroups: state.selectedGroups.filter((_, i) => i !== index),
  };
}
